/**
 * SuperAdminPreviewContext
 * ────────────────────────
 * Mode "Voir comme cet utilisateur" — preview visuelle strictement contrôlée.
 *
 * Garanties :
 * - La session Supabase Auth du SuperAdmin reste INCHANGÉE (auth.uid() côté serveur
 *   reste celle du SuperAdmin). Les RLS continuent d'évaluer le SuperAdmin réel.
 * - Ce contexte ne fait QUE simuler le rôle / profil cible côté UI (routing, menus,
 *   permissions calculées par `canPerform`).
 * - Toutes les actions d'écriture sensibles doivent se gater via `useIsPreviewMode()`.
 *
 * Persistance : sessionStorage uniquement (le mode n'est PAS partagé entre onglets,
 * ni rétabli après fermeture du navigateur).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth, type UserProfile, type UserRole } from "@/contexts/AuthContext";

const STORAGE_KEY = "lyko_superadmin_preview_v1";

export interface PreviewState {
  sessionId: string;        // id retourné par start_user_preview RPC
  targetUserId: string;
  targetRole: UserRole;
  targetProfile?: Partial<UserProfile> | null;
  targetLabel: string;      // affiché dans le bandeau
  startedAt: string;
}

interface PreviewContextValue {
  preview: PreviewState | null;
  isPreviewing: boolean;
  setPreview: (p: PreviewState) => void;
  clearPreview: () => void;
}

const SuperAdminPreviewContext = createContext<PreviewContextValue | undefined>(undefined);

function loadFromStorage(): PreviewState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PreviewState;
  } catch {
    return null;
  }
}

export function SuperAdminPreviewProvider({ children }: { children: ReactNode }) {
  const [preview, setPreviewState] = useState<PreviewState | null>(() => loadFromStorage());

  const setPreview = useCallback((p: PreviewState) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    setPreviewState(p);
  }, []);

  const clearPreview = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPreviewState(null);
  }, []);

  // Sync between tabs (defensive — should not happen since sessionStorage isn't shared,
  // but covers edge-case duplication).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setPreviewState(e.newValue ? JSON.parse(e.newValue) : null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo<PreviewContextValue>(
    () => ({ preview, isPreviewing: !!preview, setPreview, clearPreview }),
    [preview, setPreview, clearPreview]
  );

  return (
    <SuperAdminPreviewContext.Provider value={value}>
      {children}
    </SuperAdminPreviewContext.Provider>
  );
}

export function useSuperAdminPreview() {
  const ctx = useContext(SuperAdminPreviewContext);
  if (!ctx) throw new Error("useSuperAdminPreview must be used within SuperAdminPreviewProvider");
  return ctx;
}

/**
 * Blocage d'action en mode preview.
 * À utiliser en remplacement / complément de `canPerform` partout où une action
 * d'écriture est exposée à l'utilisateur (boutons, mutations, etc.).
 */
export function useIsPreviewMode(): boolean {
  return useSuperAdminPreview().isPreviewing;
}

export function usePreviewAwareAuth() {
  const auth = useAuth();
  const { preview, isPreviewing, clearPreview } = useSuperAdminPreview();

  // SECURITY — auto-clear preview if the real auth user id changes
  // (e.g. session swap after /set-password verifyOtp, sign-in as another
  // account, etc). Prevents a stale SuperAdmin preview from surviving
  // a session change and granting role-display privileges on the new
  // identity. Tracks the last seen real user id in a ref to fire only
  // on actual transitions.
  const lastRealUserIdRef = useRef<string | null | undefined>(undefined);
  const realUserId = auth.user?.id ?? null;
  useEffect(() => {
    if (lastRealUserIdRef.current === undefined) {
      lastRealUserIdRef.current = realUserId;
      return;
    }
    if (lastRealUserIdRef.current !== realUserId) {
      if (isPreviewing) {
        // eslint-disable-next-line no-console
        console.warn("[SuperAdminPreview] real user changed — clearing preview", {
          previous: lastRealUserIdRef.current,
          current: realUserId,
        });
        clearPreview();
      }
      lastRealUserIdRef.current = realUserId;
    }
  }, [realUserId, isPreviewing, clearPreview]);

  // ⚠️ IMPORTANT: previewProfile DOIT être mémoïsé.
  // Sinon un nouvel objet est recréé à chaque render → identités instables sur
  // `userProfile` → boucles infinies dans les hooks consommateurs (useEffect,
  // useCallback) qui dépendent de `userProfile`. Symptôme observé : skeletons
  // infinis sur le dashboard en mode preview + cascades "Failed to fetch".
  const previewProfile = useMemo<UserProfile | null>(() => {
    if (!isPreviewing) return auth.userProfile;
    if (!preview?.targetProfile) return auth.userProfile;
    return {
      ...(auth.userProfile ?? {}),
      ...preview.targetProfile,
      id: preview.targetUserId,
    } as UserProfile;
  }, [isPreviewing, preview?.targetProfile, preview?.targetUserId, auth.userProfile]);

  const effectiveRole = isPreviewing ? preview?.targetRole ?? null : auth.userRole;
  const effectiveUserId = isPreviewing ? preview?.targetUserId ?? null : auth.user?.id ?? null;

  return {
    ...auth,
    realUserId: auth.user?.id ?? null,
    effectiveUserId,
    effectiveRole,
    userRole: effectiveRole,
    userProfile: previewProfile,
    isPreviewing,
    preview,
    previewTargetUserId: preview?.targetUserId ?? null,
    previewTargetRole: preview?.targetRole ?? null,
  };
}
